/**
 * The farm-entry loading card.
 *
 * Tapping Farm used to drop the player on a flat `#3f7fb8` rectangle — the stage's
 * clear colour with nothing on it — for as long as ~600 sprites, a fresh WebGL
 * context, a 1.27 MB draft decode, a ~310k-tile world generation and a dozen HTTP
 * round trips took. Players reported that blue as a bug, and on slow internet they
 * were right to: nothing on screen said the game was working.
 *
 * So this is an honest report, not decoration. Every number the bar shows comes
 * from a stage that actually finished — there is deliberately NO timer driving the
 * fill (the arena's card at arena-br.ts:493 does that, and it lies whenever the
 * real load is faster or slower than its 2600 ms guess). If a stage stalls, the bar
 * stalls with it, which is the truth the player needs.
 */
import { el } from '../../dom';

/**
 * The stages, in the order farm.ts awaits them, with the share of the bar each one
 * owns. The weights are a rough map of where the wall-clock goes on a cold entry:
 * sprites dominate, world generation is the second big block. They must sum to 1 —
 * `farmLoadFraction` is only monotonic across the declared order if they do, and
 * the unit test asserts it.
 */
export const FARM_LOAD_STAGES = [
  { key: 'sprites', label: 'Loading sprites', weight: 0.34 },
  { key: 'renderer', label: 'Starting the renderer', weight: 0.06 },
  { key: 'farm', label: 'Reading your farm', weight: 0.10 },
  { key: 'map', label: 'Loading the map', weight: 0.14 },
  { key: 'world', label: 'Growing the world', weight: 0.21 },
  { key: 'valley', label: 'Waking the valley', weight: 0.15 },
] as const;

export type FarmLoadStage = (typeof FARM_LOAD_STAGES)[number]['key'];

/** Everything before `key`, summed — the fraction already earned when it starts. */
function completedWeight(key: FarmLoadStage): number {
  let sum = 0;
  for (const s of FARM_LOAD_STAGES) {
    if (s.key === key) break;
    sum += s.weight;
  }
  return sum;
}

function stageWeight(key: FarmLoadStage): number {
  return FARM_LOAD_STAGES.find((s) => s.key === key)?.weight ?? 0;
}

export function farmLoadLabel(key: FarmLoadStage): string {
  return FARM_LOAD_STAGES.find((s) => s.key === key)?.label ?? '';
}

/**
 * Pure: where the bar sits when `key` is `sub` (0..1) of the way through.
 *
 * Kept free of the DOM so the state machine can be unit-tested on its own — jsdom
 * has no layout engine, so a test that measured the bar would prove nothing.
 */
export function farmLoadFraction(key: FarmLoadStage, sub = 0): number {
  const f = Math.min(1, Math.max(0, sub));
  return completedWeight(key) + stageWeight(key) * f;
}

/**
 * The bar must never walk backwards, and must never read 100% while work is still
 * running: a stage that reports a lower fraction than one already painted (a warm
 * Pixi cache resolving `sprites` instantly, say) is clamped, and the last 2% is
 * held back for `done()`. A player who sees 100% and then waits has been lied to.
 */
export function createFarmLoadProgress() {
  let last = 0;
  return {
    set(key: FarmLoadStage, sub = 0): number {
      last = Math.max(last, Math.min(0.98, farmLoadFraction(key, sub)));
      return last;
    },
    finish(): number {
      last = 1;
      return last;
    },
    value(): number {
      return last;
    },
  };
}

export interface FarmLoader {
  el: HTMLElement;
  /** Move to `key`, optionally part-way through it. Repaints label + bar + percent. */
  stage(key: FarmLoadStage, sub?: number): void;
  /** Swap the card into an error state with a Retry button. */
  fail(message: string, onRetry: () => void): void;
  /** Loaded: fill to 100% and fade the card out. */
  done(): void;
  /** View teardown — kill the pending timers so nothing touches a detached node. */
  destroy(): void;
}

/** Hold the card back this long: a warm re-entry finishes first and never flashes. */
const SHOW_AFTER_MS = 150;

/**
 * Build the card and attach it to `host`.
 *
 * It starts hidden. A switch that completes inside SHOW_AFTER_MS (warm textures,
 * warm world) removes it without ever painting, so a fast Shop→Farm tap does not
 * strobe a card; anything slower shows it and keeps it up until the map is there.
 */
export function createFarmLoader(host: HTMLElement): FarmLoader {
  const label = el('div', 'text-[11px] font-mono text-[#e9d6bd]/85', {}, `${farmLoadLabel('sprites')}…`);
  const pct = el('div', 'text-[11px] font-mono text-[#ffb648] tabular-nums', {}, '0%');
  const fill = el('div', 'h-full transition-[width] duration-200 ease-linear',
    { style: 'width:2%;background:linear-gradient(90deg,#f0842a,#ffd27a)' });
  const bar = el('div', 'w-full h-2 rounded-full bg-black/50 border border-[#7a3a12] overflow-hidden', {}, fill);
  const actions = el('div', 'mt-3 flex justify-center');
  const panel = el('div', 'game-font w-[280px] max-w-[80vw] px-5 py-4 rounded-lg bg-[#2a160c]/95 border border-[#7a3a12] shadow-lift text-center',
    {},
    // Same voice as the arena's card (arena-br.ts:481, 'ENTERING THE ARENA').
    el('div', 'text-[16px] tracking-wide text-[#ffb648] mb-2.5', { style: 'text-shadow: 0 2px 0 #7a1f0a' }, 'ENTERING THE VALLEY'),
    el('div', 'flex items-baseline justify-between gap-2 mb-1.5', {}, label, pct),
    bar,
    actions);
  // z above the siblings farm.ts mounts into the same wrap while the card is up:
  // the award popup (z-40) and the minimap's full-screen map (z-50).
  const card = el('div', 'absolute inset-0 z-[60] grid place-items-center p-4 transition-opacity duration-300',
    { style: 'background:#3f7fb8;opacity:0' }, panel);
  host.append(card);

  const progress = createFarmLoadProgress();
  let shown = false;
  let dead = false;
  // Set the instant done() runs, not when the fade finishes: the caller's async
  // block carries on past done() (the tutorial), and a throw in there used to be
  // caught by farm.ts and turned into fail() — a full-screen "Farm failed to load"
  // over a valley that had already rendered. A load that reached done() succeeded;
  // nothing after it may reopen the card.
  let finished = false;
  const timers: number[] = [];
  const showTimer = window.setTimeout(() => {
    if (dead) return;
    shown = true;
    card.style.opacity = '1';
  }, SHOW_AFTER_MS);
  timers.push(showTimer);

  const paint = (f: number) => {
    // Floor at 2% so the trough always reads as "started" rather than empty.
    fill.style.width = `${Math.max(2, Math.round(f * 100))}%`;
    pct.textContent = `${Math.round(f * 100)}%`;
  };

  return {
    el: card,
    stage(key, sub = 0) {
      if (dead) return;
      label.textContent = `${farmLoadLabel(key)}…`;
      paint(progress.set(key, sub));
    },
    fail(message, onRetry) {
      if (dead || finished) return;
      // A failed load must not leave a bar sitting at 61% forever, and the bare red
      // text node this replaced was painted UNDER the blue with no way out of it.
      window.clearTimeout(showTimer);
      shown = true;
      card.style.opacity = '1';
      bar.style.display = 'none';
      pct.style.display = 'none';
      label.className = 'text-[11px] font-mono text-error break-words';
      label.textContent = `Farm failed to load: ${message}`;
      clearActions();
      actions.append(el('button', 'game-font px-3 py-1.5 rounded-md text-[12px] bg-[#f0842a] text-[#2a160c] hover:bg-[#ffd27a]',
        { onClick: onRetry }, 'Retry'));
    },
    done() {
      if (dead || finished) return;
      finished = true;
      paint(progress.finish());
      if (!shown) { this.destroy(); return; }
      card.style.opacity = '0';
      // transitionend alone is not enough: a hidden tab fires no transition, and the
      // card would stay on top of the world. The timeout is the fallback, and both
      // paths go through destroy() so the node is removed exactly once.
      // Only the card's OWN opacity transition counts. transitionend bubbles, and
      // the bar's width animation (duration-200) ends first — a bare `once`
      // listener consumed it and yanked the card at roughly a third faded, which is
      // exactly the pop the show-delay exists to avoid.
      card.addEventListener('transitionend', (e) => {
        if (e.target !== card || e.propertyName !== 'opacity') return;
        this.destroy();
      });
      timers.push(window.setTimeout(() => this.destroy(), 500));
    },
    destroy() {
      dead = true;
      for (const t of timers) window.clearTimeout(t);
      card.remove();
    },
  };

  function clearActions() {
    while (actions.firstChild) actions.firstChild.remove();
  }
}
